import dayjs from "dayjs";

export interface OutageWindow {
  id: number;
  project_id?: number;
  outage_start_date: string;
  outage_end_date: string;
  status?: string;
  status_display_text?: string;
}

/**
 * Calculate the status of an outage window based on its dates
 * @param startDate Outage window start date
 * @param endDate Outage window end date
 * @returns Status: "inactive" (before start), "active" (between start and end), or "expired" (after end)
 */
export const calculateOutageWindowStatus = (startDate: string | Date, endDate: string | Date): "inactive" | "active" | "expired" => {
  const now = dayjs().startOf("day");
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).startOf("day");

  if (now.isBefore(start)) {
    return "inactive";
  } else if (now.isAfter(end)) {
    return "expired";
  } else {
    return "active";
  }
};

/**
 * Get all active outage windows from a list
 * Filters based on the status property
 * @param outageWindows List of outage windows
 * @returns Filtered list of active outage windows
 */
export const getActiveOutageWindows = (outageWindows: OutageWindow[]): OutageWindow[] => {
  if (!outageWindows || outageWindows.length === 0) return [];
  
  return outageWindows.filter((window) => {
    return window.status?.toLowerCase() === "active";
  });
};

/**
 * Check if a date can toggle from inactive to active for a specific outage window
 * (should be within the window's start and end date)
 * @param date Date to check
 * @param startDate Outage window start date
 * @param endDate Outage window end date
 * @returns true if the date is within the outage window period
 */
export const canActivateOutageWindow = (date: dayjs.Dayjs, startDate: string | Date, endDate: string | Date): boolean => {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).startOf("day");
  
  return !date.isBefore(start) && !date.isAfter(end);
};
